import React, { useState } from 'react';
import { useSiteData } from '../context/SiteDataContext';
import { toast } from 'sonner';
import { Save, LogOut, Settings, Layout, DollarSign, Image as ImageIcon, Users, MessageCircle, PenTool, PhoneCall, ListOrdered, Lock, Shield, AlertTriangle, Layers, Activity, Plus, Trash2, HelpCircle, ChevronRight, RefreshCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

// Simplified Tabs components for Admin panel
const Tabs = ({ children, defaultValue, className }) => {
    const [activeTab, setActiveTab] = useState(defaultValue);
    return (
        <div className={className}>
            {React.Children.map(children, child => {
                if (child.type === TabsList) {
                    return React.cloneElement(child, { activeTab, setActiveTab });
                }
                if (child.type === TabsContent) {
                    if (child.props.value === activeTab) {
                        return child;
                    }
                    return null;
                }
                return child;
            })}
        </div>
    );
};

const TabsList = ({ children, activeTab, setActiveTab, className }) => (
    <div className={`inline-flex flex-wrap lg:flex-nowrap items-center justify-start space-x-1 lg:space-x-2 bg-gray-100/80 p-1.5 rounded-xl mb-8 w-full border border-gray-200/60 overflow-x-auto hide-scrollbar ${className || ''}`}>
        {React.Children.map(children, child => React.cloneElement(child, { activeTab, setActiveTab }))}
    </div>
);

const TabsTrigger = ({ children, value, activeTab, setActiveTab, className }) => {
    const isActive = activeTab === value;
    const isDanger = value === 'danger';

    return (
        <button
            className={`flex flex-1 lg:flex-none items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap ${isActive
                ? (isDanger ? 'bg-red-50 text-red-700 shadow-sm ring-1 ring-red-200' : 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200')
                : (isDanger ? 'text-red-500 hover:text-red-700 hover:bg-red-50/50' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200/50')
                } ${className || ''}`}
            onClick={() => setActiveTab(value)}
        >
            {children}
        </button>
    );
};

const TabsContent = ({ children, value }) => (
    <div className="py-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
        {children}
    </div>
);

const AdminPanel = () => {
    const { siteData, saveSiteData, resetSiteData } = useSiteData();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [formData, setFormData] = useState(siteData);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedCategoryTab, setSelectedCategoryTab] = useState('oncology');

    // Unified deep sync formData with siteData
    React.useEffect(() => {
        if (siteData && siteData.categoryPages) {
            setFormData(prev => {
                if (!prev || Object.keys(prev).length === 0) return siteData;

                const mergedCategoryPages = { ...siteData.categoryPages };

                Object.keys(siteData.categoryPages || {}).forEach(key => {
                    const baseCat = siteData.categoryPages[key] || {};
                    const prevCat = prev.categoryPages?.[key] || {};

                    const baseProducts = baseCat.products || [];
                    const prevProducts = prevCat.products || [];

                    // Smart merge: keep previous products (edits), append any new base products missing by ID
                    const mergedProducts = [...prevProducts];
                    baseProducts.forEach(bp => {
                        if (!mergedProducts.find(p => p.id === bp.id)) {
                            mergedProducts.push(bp);
                        }
                    });

                    mergedCategoryPages[key] = {
                        ...baseCat,
                        ...prevCat,
                        products: mergedProducts
                    };
                });

                return {
                    ...siteData,
                    ...prev,
                    categoryPages: mergedCategoryPages
                };
            });
        }
    }, [siteData]);

    // Recovery / Forgot Password State
    const [recoveryStep, setRecoveryStep] = useState('login'); // 'login', 'request-otp', 'verify-otp', 'reset-password'
    const [recoveryEmail, setRecoveryEmail] = useState('');
    const [sentOtp, setSentOtp] = useState('');
    const [userEnteredOtp, setUserEnteredOtp] = useState('');
    const [resetForm, setResetForm] = useState({ newPass: '', confirmPass: '' });
    const [isSendingOtp, setIsSendingOtp] = useState(false);

    // Use password from siteData if available, otherwise fallback to default
    const ADMIN_PASS = siteData.general?.adminPassword || 'mediseller@admin2025';

    const [securityForm, setSecurityForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const handleLogin = (e) => {
        e.preventDefault();
        if (password === ADMIN_PASS) {
            setIsAuthenticated(true);
            toast.success('Login Successful');
        } else {
            toast.error('Invalid Password');
        }
    };

    const handleLogout = () => {
        setIsAuthenticated(false);
        setPassword('');
    };

    const updateField = (path, value) => {
        setFormData(prev => {
            const newData = { ...prev };
            const keys = path.split('.');
            let current = newData;

            for (let i = 0; i < keys.length - 1; i++) {
                // Create nested object if it doesn't exist
                if (!current[keys[i]]) current[keys[i]] = {};
                current = current[keys[i]];
            }

            current[keys[keys.length - 1]] = value;
            return newData;
        });
    };

    const updateArrayItem = (path, index, field, value) => {
        setFormData(prev => {
            const newData = { ...prev };
            const arrayRef = path.split('.').reduce((obj, key) => obj[key], newData);
            if (Array.isArray(arrayRef)) {
                arrayRef[index] = { ...arrayRef[index], [field]: value };
            }
            return newData;
        });
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const success = saveSiteData(formData);
            if (success) {
                toast.success("Site configuration saved successfully!");
            } else {
                toast.error("Failed to save changes.");
            }
        } catch (error) {
            toast.error("An error occurred while saving.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        if (window.confirm("Are you sure you want to revert ALL changes to the default factory settings? This cannot be undone.")) {
            resetSiteData();
            toast.success("Reset to default settings complete. Please refresh or reload data.");
            setTimeout(() => window.location.reload(), 1000);
        }
    };

    const handlePasswordChange = (e) => {
        e.preventDefault();

        if (securityForm.currentPassword !== ADMIN_PASS) {
            toast.error("Current password is incorrect.");
            return;
        }

        if (securityForm.newPassword !== securityForm.confirmPassword) {
            toast.error("New passwords do not match.");
            return;
        }

        if (securityForm.newPassword.length < 6) {
            toast.error("New password must be at least 6 characters long.");
            return;
        }

        // Update the password in formData
        updateField('general.adminPassword', securityForm.newPassword);

        // Reset form
        setSecurityForm({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });

        toast.success("Password updated in form data! Please save all changes to make it permanent.");
    };

    // FORGOT PASSWORD SCREENS
    if (recoveryStep !== 'login' && !isAuthenticated) {
        const handleRequestOtp = async (e) => {
            e.preventDefault();

            const registeredEmail = siteData.general?.recoveryEmail;
            if (!registeredEmail) {
                toast.error("No recovery email configured. Please contact administrator.");
                return;
            }

            if (recoveryEmail.toLowerCase().trim() !== registeredEmail.toLowerCase().trim()) {
                toast.error("Email does not match our records.");
                return;
            }

            setIsSendingOtp(true);
            const generatedOtp = Math.floor(100000 + Math.random() * 900000).toString();

            try {
                // Call Google Script to send email
                await fetch(siteData.general?.googleSheetUrl, {
                    method: 'POST',
                    mode: 'no-cors',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams({
                        action: 'sendOTP',
                        email: registeredEmail,
                        otp: generatedOtp,
                        siteName: siteData.general?.siteName || 'MediSeller'
                    })
                });

                setSentOtp(generatedOtp);
                setRecoveryStep('verify-otp');
                toast.success(`Verification code sent to ${registeredEmail}`);
            } catch (error) {
                toast.error("Failed to send verification code. Check your Google Sheet URL.");
                console.error(error);
            } finally {
                setIsSendingOtp(false);
            }
        };

        const handleVerifyOtp = (e) => {
            e.preventDefault();
            if (userEnteredOtp === sentOtp) {
                setRecoveryStep('reset-password');
                toast.success("OTP verified!");
            } else {
                toast.error("Invalid verification code.");
            }
        };

        const handleResetPass = (e) => {
            e.preventDefault();
            if (resetForm.newPass.length < 6) {
                toast.error("Password must be at least 6 characters.");
                return;
            }
            if (resetForm.newPass !== resetForm.confirmPass) {
                toast.error("Passwords do not match.");
                return;
            }

            // Directly update siteData and localStorage for reset
            const updatedData = {
                ...siteData,
                general: {
                    ...siteData.general,
                    adminPassword: resetForm.newPass
                }
            };
            saveSiteData(updatedData);
            setFormData(updatedData); // Sync local state

            toast.success("Password reset successfully! Please sign in.");
            setRecoveryStep('login');
            setPassword(''); // Clear login password field
        };

        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="flex justify-center flex-col items-center">
                        <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center shadow-lg mb-4">
                            <Lock className="text-white w-8 h-8" />
                        </div>
                        <h2 className="mt-2 text-center text-3xl font-extrabold text-gray-900">
                            {recoveryStep === 'request-otp' && "Recover Admin Access"}
                            {recoveryStep === 'verify-otp' && "Verify Identity"}
                            {recoveryStep === 'reset-password' && "Set New Password"}
                        </h2>
                    </div>

                    <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md px-4 sm:px-0">
                        <div className="bg-white py-8 px-8 shadow-xl rounded-2xl border border-gray-100">
                            {recoveryStep === 'request-otp' && (
                                <form className="space-y-6" onSubmit={handleRequestOtp}>
                                    <div>
                                        <Label htmlFor="rec-email">Recovery Email Address</Label>
                                        <p className="text-xs text-gray-500 mb-3">OTP will be sent to the email registered in site settings.</p>
                                        <Input
                                            id="rec-email"
                                            type="email"
                                            required
                                            value={recoveryEmail}
                                            onChange={(e) => setRecoveryEmail(e.target.value)}
                                            placeholder="admin@example.com"
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <Button type="submit" disabled={isSendingOtp} className="w-full bg-red-600 hover:bg-red-700">
                                            {isSendingOtp ? "Sending OTP..." : "Send Verification Code"}
                                        </Button>
                                        <button
                                            type="button"
                                            onClick={() => setRecoveryStep('login')}
                                            className="w-full text-sm text-gray-500 hover:text-gray-700 font-medium"
                                        >
                                            Back to Login
                                        </button>
                                    </div>
                                </form>
                            )}

                            {recoveryStep === 'verify-otp' && (
                                <form className="space-y-6" onSubmit={handleVerifyOtp}>
                                    <div>
                                        <Label htmlFor="otp">Enter 6-Digit Code</Label>
                                        <p className="text-xs text-gray-500 mb-3">Check your inbox for the verification code.</p>
                                        <Input
                                            id="otp"
                                            required
                                            placeholder="000000"
                                            value={userEnteredOtp}
                                            onChange={(e) => setUserEnteredOtp(e.target.value)}
                                            className="text-center text-2xl tracking-widest font-bold h-14"
                                            maxLength={6}
                                        />
                                    </div>
                                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700">
                                        Verify OTP
                                    </Button>
                                    <p className="text-center text-xs text-gray-400">
                                        Didn't receive code? Try checking Spam or re-sending.
                                    </p>
                                </form>
                            )}

                            {recoveryStep === 'reset-password' && (
                                <form className="space-y-6" onSubmit={handleResetPass}>
                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="new-p">New Admin Password</Label>
                                            <Input
                                                id="new-p"
                                                type="password"
                                                required
                                                value={resetForm.newPass}
                                                onChange={(e) => setResetForm({ ...resetForm, newPass: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="conf-p">Confirm New Password</Label>
                                            <Input
                                                id="conf-p"
                                                type="password"
                                                required
                                                value={resetForm.confirmPass}
                                                onChange={(e) => setResetForm({ ...resetForm, confirmPass: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <Button type="submit" className="w-full bg-green-600 hover:bg-green-700">
                                        Update Password & Sign In
                                    </Button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="flex justify-center flex-col items-center">
                        <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center shadow-lg mb-4">
                            <span className="text-white font-bold text-3xl">M</span>
                        </div>
                        <h2 className="mt-2 text-center text-3xl font-extrabold text-gray-900">Admin Portal</h2>
                    </div>

                    <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                            <form className="space-y-6" onSubmit={handleLogin}>
                                <div>
                                    <Label htmlFor="password">Admin Password</Label>
                                    <div className="mt-1">
                                        <Input
                                            id="password"
                                            name="password"
                                            type="password"
                                            autoComplete="current-password"
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <Button type="submit" className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                                        Sign in
                                    </Button>
                                    <div className="mt-4 text-center">
                                        <button
                                            type="button"
                                            onClick={() => setRecoveryStep('request-otp')}
                                            className="text-sm font-medium text-red-600 hover:text-red-500 transition-colors cursor-pointer"
                                        >
                                            Forgot password?
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Top Navigation */}
            <nav className="bg-white/90 backdrop-blur-md shadow-sm border-b border-gray-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 flex items-center space-x-2">
                                <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center shadow-md">
                                    <span className="text-white font-bold">M</span>
                                </div>
                                <span className="font-bold text-xl text-gray-900 tracking-tight">MediSeller Admin</span>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <Button onClick={() => window.open('/', '_blank')} variant="outline" className="text-sm border-gray-300">
                                View Live Site
                            </Button>
                            <Button
                                onClick={handleReset}
                                variant="outline"
                                className="text-sm border-red-200 text-red-600 hover:bg-red-50"
                            >
                                <RefreshCcw className="w-4 h-4 mr-2" />
                                Reset to Defaults
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="bg-green-600 hover:bg-green-700 text-white flex items-center space-x-2 shadow-sm"
                            >
                                <Save className="w-4 h-4" />
                                <span>{isSaving ? "Saving..." : "Save All Changes"}</span>
                            </Button>
                            <Button onClick={handleLogout} variant="ghost" className="text-gray-500 hover:text-red-600 bg-gray-50">
                                <LogOut className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>
                </div>
            </nav>

            <style>{`
                .hide-scrollbar::-webkit-scrollbar { display: none; }
                .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>

            {/* Main Content Dashboard */}
            <div className="max-w-7xl mx-auto py-8 sm:px-6 lg:px-8">
                <div className="bg-white shadow-xl rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="p-6 sm:p-10">
                        <Tabs defaultValue="general">
                            <TabsList>
                                <TabsTrigger value="general"><Settings className="w-4 h-4 mr-2" /> General Info</TabsTrigger>
                                <TabsTrigger value="hero"><Layout className="w-4 h-4 mr-2" /> Homepage Hero</TabsTrigger>
                                <TabsTrigger value="sections"><Layers className="w-4 h-4 mr-2" /> Section Headings</TabsTrigger>
                                <TabsTrigger value="featureBlocks"><ListOrdered className="w-4 h-4 mr-2" /> Cards & Lists</TabsTrigger>
                                <TabsTrigger value="contactForms"><PhoneCall className="w-4 h-4 mr-2" /> Forms & CTA</TabsTrigger>
                                <TabsTrigger value="footerTestimonial"><PenTool className="w-4 h-4 mr-2" /> Additional</TabsTrigger>
                                <TabsTrigger value="diseasePages"><Activity className="w-4 h-4 mr-2" /> Disease Pages</TabsTrigger>
                                <TabsTrigger value="security"><Lock className="w-4 h-4 mr-2" /> Security</TabsTrigger>
                                <TabsTrigger value="danger"><AlertTriangle className="w-4 h-4 mr-2" /> Advanced</TabsTrigger>
                            </TabsList>

                            {/* GENERAL TAB */}
                            <TabsContent value="general">
                                <div className="space-y-6 max-w-4xl">
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-2">General Information</h3>
                                        <p className="text-sm text-gray-500 border-b border-gray-100 pb-6">Update the core site details, contact numbers, and branding strings.</p>
                                    </div>

                                    <div className="grid grid-cols-1 gap-y-8 gap-x-6 sm:grid-cols-6 pt-2">
                                        <div className="sm:col-span-3">
                                            <Label>Site Name</Label>
                                            <Input
                                                value={formData.general?.siteName || ''}
                                                onChange={(e) => updateField('general.siteName', e.target.value)}
                                                className="mt-1"
                                            />
                                        </div>

                                        <div className="sm:col-span-3">
                                            <Label>Tagline</Label>
                                            <Input
                                                value={formData.general?.tagline || ''}
                                                onChange={(e) => updateField('general.tagline', e.target.value)}
                                                className="mt-1"
                                            />
                                        </div>

                                        <div className="sm:col-span-3">
                                            <Label>WhatsApp Number (Numeric only with Country Code)</Label>
                                            <div className="mt-1 flex rounded-md shadow-sm">
                                                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                                                    +
                                                </span>
                                                <Input
                                                    value={formData.general?.whatsappNumber || ''}
                                                    onChange={(e) => updateField('general.whatsappNumber', e.target.value)}
                                                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md"
                                                />
                                            </div>
                                        </div>

                                        <div className="sm:col-span-3">
                                            <Label>Support Email</Label>
                                            <Input
                                                type="email"
                                                value={formData.general?.supportEmail || ''}
                                                onChange={(e) => updateField('general.supportEmail', e.target.value)}
                                                className="mt-1"
                                            />
                                        </div>
                                        <div className="sm:col-span-3">
                                            <Label>Header Top Right CTA Button Text</Label>
                                            <Input
                                                value={formData.general?.headerCtaText || ''}
                                                onChange={(e) => updateField('general.headerCtaText', e.target.value)}
                                                className="mt-1"
                                            />
                                        </div>

                                        <div className="sm:col-span-3">
                                            <Label>Header Top Right CTA Link</Label>
                                            <Input
                                                value={formData.general?.headerCtaLink || ''}
                                                onChange={(e) => updateField('general.headerCtaLink', e.target.value)}
                                                className="mt-1"
                                                placeholder="#contact or https://..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>

                            {/* HERO TAB */}
                            <TabsContent value="hero">
                                <div className="space-y-6 max-w-4xl">
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Homepage Hero Banner</h3>
                                        <p className="text-sm text-gray-500 border-b border-gray-100 pb-6">Control what visitors see immediately when they land on your site.</p>
                                    </div>

                                    <div className="pt-2 space-y-8">
                                        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200/60">
                                            <Label className="text-gray-700 font-bold bg-yellow-100 px-2 py-1 rounded">H1 Tag (Main Title - Primary Focus)</Label>
                                            <p className="text-xs text-gray-500 mt-1 mb-2">Broken into 3 parts for styling. Part 2 is the Red Highlight.</p>
                                            <div className="flex flex-col sm:flex-row gap-2">
                                                <Input
                                                    placeholder="Title Part 1"
                                                    value={formData.minimalHero?.titlePart1 || ''}
                                                    onChange={(e) => updateField('minimalHero.titlePart1', e.target.value)}
                                                />
                                                <Input
                                                    placeholder="Highlight (Red Text)"
                                                    value={formData.minimalHero?.titleHighlight || ''}
                                                    onChange={(e) => updateField('minimalHero.titleHighlight', e.target.value)}
                                                />
                                                <Input
                                                    placeholder="Title Part 3 (Optional)"
                                                    value={formData.hero?.titlePart2 || ''}
                                                    onChange={(e) => updateField('hero.titlePart2', e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200/60">
                                            <Label className="text-gray-700 font-bold bg-blue-100 px-3 py-1.5 rounded-md inline-block mb-2">H2 Tag (Subtitle & Description)</Label>
                                            <Textarea
                                                rows={3}
                                                value={formData.minimalHero?.subtitle || ''}
                                                onChange={(e) => updateField('minimalHero.subtitle', e.target.value)}
                                                className="mt-2 bg-white"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-gray-50 p-6 rounded-xl border border-gray-200/60">
                                            <div>
                                                <Label className="text-sm font-semibold">Primary CTA Button</Label>
                                                <Input
                                                    value={formData.minimalHero?.cta1Text || ''}
                                                    onChange={(e) => updateField('minimalHero.cta1Text', e.target.value)}
                                                    className="mt-1 mb-2"
                                                />
                                                <Label className="text-sm font-semibold text-gray-500">Primary CTA Link</Label>
                                                <Input
                                                    value={formData.minimalHero?.cta1Link || ''}
                                                    onChange={(e) => updateField('minimalHero.cta1Link', e.target.value)}
                                                    className="mt-1"
                                                    placeholder="#diseases, /page, or https://..."
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-sm font-semibold">Secondary CTA Button</Label>
                                                <Input
                                                    value={formData.minimalHero?.cta2Text || ''}
                                                    onChange={(e) => updateField('minimalHero.cta2Text', e.target.value)}
                                                    className="mt-1 mb-2"
                                                />
                                                <Label className="text-sm font-semibold text-gray-500">Secondary CTA Link</Label>
                                                <Input
                                                    value={formData.minimalHero?.cta2Link || ''}
                                                    onChange={(e) => updateField('minimalHero.cta2Link', e.target.value)}
                                                    className="mt-1"
                                                    placeholder="#contact, /page, or https://..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>

                            {/* SECTIONS TAB */}
                            <TabsContent value="sections">
                                <div className="space-y-6 max-w-5xl">
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Page Sections Configuration</h3>
                                        <p className="text-sm text-gray-500 border-b border-gray-100 pb-6">Edit titles and subtitles for every middle section on the page.</p>
                                    </div>

                                    <div className="pt-2 grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Categories */}
                                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                            <h4 className="font-bold text-md mb-2">Diseases & Categories Section</h4>
                                            <Label>Title</Label>
                                            <Input value={formData.sections?.categoriesHeader || ''} onChange={(e) => updateField('sections.categoriesHeader', e.target.value)} className="mb-2" />
                                            <Label>Subtitle</Label>
                                            <Input value={formData.sections?.categoriesSub || ''} onChange={(e) => updateField('sections.categoriesSub', e.target.value)} />
                                        </div>

                                        {/* Savings */}
                                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                            <h4 className="font-bold text-md mb-2">Cost Savings Section</h4>
                                            <Label>Title</Label>
                                            <Input value={formData.sections?.savingsHeader || ''} onChange={(e) => updateField('sections.savingsHeader', e.target.value)} className="mb-2" />
                                            <Label>Subtitle</Label>
                                            <Input value={formData.sections?.savingsSub || ''} onChange={(e) => updateField('sections.savingsSub', e.target.value)} />
                                        </div>

                                        {/* How it works */}
                                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                            <h4 className="font-bold text-md mb-2">How It Works Section</h4>
                                            <Label>Title</Label>
                                            <Input value={formData.sections?.howItWorksHeader || ''} onChange={(e) => updateField('sections.howItWorksHeader', e.target.value)} className="mb-2" />
                                            <Label>Subtitle</Label>
                                            <Input value={formData.sections?.howItWorksSub || ''} onChange={(e) => updateField('sections.howItWorksSub', e.target.value)} />
                                        </div>

                                        {/* Consultation Form */}
                                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                            <h4 className="font-bold text-md mb-2">Consultation Form</h4>
                                            <Label>Title</Label>
                                            <Input value={formData.sections?.consultationHeader || ''} onChange={(e) => updateField('sections.consultationHeader', e.target.value)} className="mb-2" />
                                            <Label>Subtitle</Label>
                                            <Input value={formData.sections?.consultationSub || ''} onChange={(e) => updateField('sections.consultationSub', e.target.value)} />
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>

                            {/* FEATURE BLOCKS TAB (Cards and Arrays) */}
                            <TabsContent value="featureBlocks">
                                <div className="space-y-8 max-w-5xl">
                                    {/* Disease Categories */}
                                    <div>
                                        <div className="flex justify-between items-center border-b pb-2 mb-4">
                                            <div>
                                                <h3 className="text-lg font-medium leading-6 text-gray-900">Disease Categories</h3>
                                                <p className="text-xs text-gray-500 mt-1">Edit the main category cards shown on the homepage.</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                            {formData.categories?.map((category, index) => (
                                                <div key={index} className="bg-gray-50 p-4 border border-gray-200 rounded-xl relative hover:border-red-300 transition-colors">
                                                    <div className="absolute -top-3 -left-3 w-8 h-8 bg-white border border-gray-200 text-gray-500 rounded-full flex items-center justify-center font-bold shadow-sm">
                                                        {index + 1}
                                                    </div>

                                                    <div className="space-y-3 pt-2">
                                                        <div>
                                                            <Label className="text-xs font-semibold text-gray-600">Category Name</Label>
                                                            <Input
                                                                value={category.name}
                                                                onChange={(e) => updateArrayItem('categories', index, 'name', e.target.value)}
                                                                className="mt-1 h-9 bg-white"
                                                            />
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <div>
                                                                <Label className="text-xs font-semibold text-gray-600">Icon Name</Label>
                                                                <Input
                                                                    value={category.icon}
                                                                    onChange={(e) => updateArrayItem('categories', index, 'icon', e.target.value)}
                                                                    className="mt-1 h-9 bg-white"
                                                                    placeholder="e.g. Heart"
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label className="text-xs font-semibold text-gray-600">ID / URL Path</Label>
                                                                <Input
                                                                    value={category.id}
                                                                    onChange={(e) => updateArrayItem('categories', index, 'id', e.target.value)}
                                                                    className="mt-1 h-9 bg-white"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <div>
                                                                <Label className="text-xs font-semibold text-gray-600">Medications Count</Label>
                                                                <Input
                                                                    type="number"
                                                                    value={category.productCount}
                                                                    onChange={(e) => updateArrayItem('categories', index, 'productCount', parseInt(e.target.value))}
                                                                    className="mt-1 h-9 bg-white"
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label className="text-xs font-semibold text-gray-600">Savings Text</Label>
                                                                <Input
                                                                    value={category.avgSavings}
                                                                    onChange={(e) => updateArrayItem('categories', index, 'avgSavings', e.target.value)}
                                                                    className="mt-1 h-9 bg-white"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Cost Savings */}
                                    <div className="pt-8 border-t border-gray-200">
                                        <div className="flex justify-between items-center border-b pb-2 mb-4">
                                            <div>
                                                <h3 className="text-lg font-medium leading-6 text-gray-900">Cost Savings Table</h3>
                                                <p className="text-xs text-gray-500 mt-1">Edit the US vs Indian generic price comparisons.</p>
                                            </div>
                                        </div>
                                        <div className="space-y-4">
                                            {formData.savings?.map((item, index) => (
                                                <div key={index} className="bg-gray-50 p-4 border border-gray-200 rounded-xl relative hover:border-red-300 transition-colors">
                                                    <div className="absolute -top-3 -left-3 w-8 h-8 bg-white border border-gray-200 text-gray-500 rounded-full flex items-center justify-center font-bold shadow-sm">
                                                        {index + 1}
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 pt-2">
                                                        <div className="md:col-span-2">
                                                            <Label className="text-xs font-semibold text-gray-600">Category</Label>
                                                            <Input
                                                                value={item.category}
                                                                onChange={(e) => updateArrayItem('savings', index, 'category', e.target.value)}
                                                                className="mt-1 h-9 bg-white"
                                                            />
                                                            <Label className="text-xs font-semibold text-gray-600 mt-2 block">Medication Name</Label>
                                                            <Input
                                                                value={item.medication}
                                                                onChange={(e) => updateArrayItem('savings', index, 'medication', e.target.value)}
                                                                className="mt-1 h-9 bg-white"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs font-semibold text-gray-600">US Price</Label>
                                                            <Input
                                                                value={item.usPrice}
                                                                onChange={(e) => updateArrayItem('savings', index, 'usPrice', e.target.value)}
                                                                className="mt-1 h-9 bg-white"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs font-semibold text-gray-600">Our Price</Label>
                                                            <Input
                                                                value={item.indianPrice}
                                                                onChange={(e) => updateArrayItem('savings', index, 'indianPrice', e.target.value)}
                                                                className="mt-1 h-9 bg-white"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs font-semibold text-gray-600">Total Savings</Label>
                                                            <Input
                                                                value={item.savings}
                                                                onChange={(e) => updateArrayItem('savings', index, 'savings', e.target.value)}
                                                                className="mt-1 h-9 bg-white"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs font-semibold text-gray-600">% Less</Label>
                                                            <Input
                                                                value={item.savingsPercent}
                                                                onChange={(e) => updateArrayItem('savings', index, 'savingsPercent', e.target.value)}
                                                                className="mt-1 h-9 bg-white"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Quality Promise */}
                                    <div className="pt-8 border-t border-gray-200">
                                        <h3 className="text-lg font-medium leading-6 text-gray-900 border-b pb-2 mb-4">Quality Promise Cards</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                            {formData.qualityPromise?.map((card, index) => (
                                                <div key={index} className="bg-gray-50 p-4 border rounded-lg">
                                                    <Label className="font-bold text-red-600 mb-2 block">Card {index + 1}</Label>
                                                    <Label>Icon Name</Label>
                                                    <Input value={card.icon || ''} onChange={(e) => updateArrayItem('qualityPromise', index, 'icon', e.target.value)} className="mb-3" placeholder="e.g. ShieldCheck" />
                                                    <Label>Title</Label>
                                                    <Input value={card.title} onChange={(e) => updateArrayItem('qualityPromise', index, 'title', e.target.value)} className="mb-3" />
                                                    <Label>Description</Label>
                                                    <Textarea rows={3} value={card.desc} onChange={(e) => updateArrayItem('qualityPromise', index, 'desc', e.target.value)} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-medium leading-6 text-gray-900 border-b pb-2 mb-4 mt-8">How it Works (Ordering Steps)</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                            {formData.howToOrderMinimal?.map((step, index) => (
                                                <div key={index} className="bg-gray-50 p-4 border rounded-lg">
                                                    <Label className="font-bold text-red-600 mb-2 block">Step {step.step}</Label>
                                                    <Label>Title</Label>
                                                    <Input value={step.title} onChange={(e) => updateArrayItem('howToOrderMinimal', index, 'title', e.target.value)} className="mb-3" />
                                                    <Label>Description</Label>
                                                    <Textarea rows={3} value={step.desc} onChange={(e) => updateArrayItem('howToOrderMinimal', index, 'desc', e.target.value)} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>

                            {/* FORMS & CTAS TAB */}
                            <TabsContent value="contactForms">
                                <div className="space-y-6 max-w-4xl">
                                    {/* Google Sheets Integration */}
                                    <div className="bg-blue-50 border border-blue-200 p-6 rounded-xl mb-8">
                                        <h4 className="text-lg font-bold text-blue-900 mb-2">Google Sheets Integration</h4>
                                        <p className="text-sm text-blue-700 mb-4">
                                            Enter your Google Apps Script Web App URL to receive form submissions directly in your Google Sheet.
                                        </p>
                                        <Label className="font-semibold text-blue-900">Web App URL</Label>
                                        <Input
                                            value={formData.general?.googleSheetUrl || ''}
                                            onChange={(e) => updateField('general.googleSheetUrl', e.target.value)}
                                            className="mt-1 bg-white border-blue-300 focus:border-blue-500 focus:ring-blue-500"
                                            placeholder="https://script.google.com/macros/s/..."
                                        />
                                    </div>

                                    <h3 className="text-lg font-medium leading-6 text-gray-900">Consultation Form Labels & Buttons</h3>
                                    <p className="mt-1 text-sm text-gray-500">Edit the input labels and placeholder text exactly as they appear to the user.</p>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200">
                                        <div className="space-y-4 bg-gray-50 p-4 rounded-lg border">
                                            <h4 className="font-bold text-md border-b pb-2">Input Labels</h4>
                                            <div><Label>Name Label</Label><Input value={formData.contactForm?.nameLabel} onChange={(e) => updateField('contactForm.nameLabel', e.target.value)} /></div>
                                            <div><Label>Email Label</Label><Input value={formData.contactForm?.emailLabel} onChange={(e) => updateField('contactForm.emailLabel', e.target.value)} /></div>
                                            <div><Label>Phone Label</Label><Input value={formData.contactForm?.phoneLabel} onChange={(e) => updateField('contactForm.phoneLabel', e.target.value)} /></div>
                                            <div><Label>Condition Label</Label><Input value={formData.contactForm?.conditionLabel} onChange={(e) => updateField('contactForm.conditionLabel', e.target.value)} /></div>
                                            <div><Label>Prescription Label</Label><Input value={formData.contactForm?.prescriptionLabel} onChange={(e) => updateField('contactForm.prescriptionLabel', e.target.value)} /></div>
                                            <div><Label>Message Label</Label><Input value={formData.contactForm?.messageLabel} onChange={(e) => updateField('contactForm.messageLabel', e.target.value)} /></div>
                                        </div>

                                        <div className="space-y-4 bg-gray-50 p-4 rounded-lg border">
                                            <h4 className="font-bold text-md border-b pb-2">Placeholders & Buttons</h4>
                                            <div><Label>Name Placeholder</Label><Input value={formData.contactForm?.namePlaceholder} onChange={(e) => updateField('contactForm.namePlaceholder', e.target.value)} /></div>
                                            <div><Label>Submit Button Text</Label><Input value={formData.contactForm?.submitBtnText} onChange={(e) => updateField('contactForm.submitBtnText', e.target.value)} /></div>
                                            <div><Label>Response Disclaimer (Bottom)</Label><Input value={formData.contactForm?.responseDisclamer} onChange={(e) => updateField('contactForm.responseDisclamer', e.target.value)} /></div>
                                            <div><Label>Chat Prompt Label</Label><Input value={formData.contactForm?.chatPrompt} onChange={(e) => updateField('contactForm.chatPrompt', e.target.value)} /></div>
                                            <div><Label>Chat Button Text</Label><Input value={formData.contactForm?.chatBtnText} onChange={(e) => updateField('contactForm.chatBtnText', e.target.value)} /></div>
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>

                            {/* FOOTER & TESTIMONIALS TAB */}
                            <TabsContent value="footerTestimonial">
                                <div className="space-y-6 max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {/* Testimonial */}
                                    <div>
                                        <h3 className="text-lg font-medium leading-6 text-gray-900 border-b pb-2 mb-4">Pinned Testimonial</h3>
                                        <div className="space-y-4 bg-gray-50 p-4 border rounded-lg">
                                            <div><Label>Patient Name</Label><Input value={formData.pinnedTestimonial?.author} onChange={(e) => updateField('pinnedTestimonial.author', e.target.value)} /></div>
                                            <div><Label>Patient Condition</Label><Input value={formData.pinnedTestimonial?.condition} onChange={(e) => updateField('pinnedTestimonial.condition', e.target.value)} /></div>
                                            <div><Label>Savings Highlight</Label><Input value={formData.pinnedTestimonial?.statistic} onChange={(e) => updateField('pinnedTestimonial.statistic', e.target.value)} /></div>
                                            <div><Label>Avatar Image URL</Label><Input value={formData.pinnedTestimonial?.image} onChange={(e) => updateField('pinnedTestimonial.image', e.target.value)} /></div>
                                            <div>
                                                <Label>Testimonial Quote</Label>
                                                <Textarea rows={5} value={formData.pinnedTestimonial?.quote} onChange={(e) => updateField('pinnedTestimonial.quote', e.target.value)} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div>
                                        <h3 className="text-lg font-medium leading-6 text-gray-900 border-b pb-2 mb-4 mt-8 md:mt-0">Footer Links & Info</h3>
                                        <div className="space-y-4 bg-gray-50 p-4 border rounded-lg">
                                            <div>
                                                <Label>Footer Description / About Text</Label>
                                                <Textarea rows={3} value={formData.footer?.description} onChange={(e) => updateField('footer.description', e.target.value)} />
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div><Label>Footer Link 1</Label><Input value={formData.footer?.link1Text} onChange={(e) => updateField('footer.link1Text', e.target.value)} /></div>
                                                <div><Label>URL</Label><Input value={formData.footer?.link1Url} onChange={(e) => updateField('footer.link1Url', e.target.value)} placeholder="#id or https://" /></div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div><Label>Footer Link 2</Label><Input value={formData.footer?.link2Text} onChange={(e) => updateField('footer.link2Text', e.target.value)} /></div>
                                                <div><Label>URL</Label><Input value={formData.footer?.link2Url} onChange={(e) => updateField('footer.link2Url', e.target.value)} placeholder="#id or https://" /></div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div><Label>Footer Link 3</Label><Input value={formData.footer?.link3Text} onChange={(e) => updateField('footer.link3Text', e.target.value)} /></div>
                                                <div><Label>URL</Label><Input value={formData.footer?.link3Url} onChange={(e) => updateField('footer.link3Url', e.target.value)} placeholder="#id or https://" /></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>

                            {/* DISEASE PAGES TAB */}
                            <TabsContent value="diseasePages">
                                <div className="space-y-8 max-w-6xl">
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
                                            <Activity className="mr-2 w-6 h-6 text-red-600" /> Disease Categories
                                        </h3>
                                        <p className="text-sm text-gray-500 border-b border-gray-100 pb-6">Edit the content and products for each specific disease category page.</p>
                                    </div>

                                    {/* Category Selector */}
                                    <div className="flex flex-wrap gap-2">
                                        {Object.keys(formData.categoryPages || {}).map(catKey => (
                                            <button
                                                key={catKey}
                                                onClick={() => setSelectedCategoryTab(catKey)}
                                                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${selectedCategoryTab === catKey
                                                    ? 'bg-red-600 text-white shadow-md'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                                                    }`}
                                            >
                                                {catKey.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Selected Category Content */}
                                    {selectedCategoryTab && formData.categoryPages?.[selectedCategoryTab] && (
                                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-8 shadow-sm">

                                            {/* Category Hero */}
                                            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                                                <h4 className="text-lg font-bold text-gray-900 mb-4 border-b pb-2 flex items-center">
                                                    <Layout className="w-5 h-5 mr-2 text-red-600" /> Hero Section ({selectedCategoryTab})
                                                </h4>
                                                <div className="grid md:grid-cols-2 gap-6 pt-2">
                                                    <div>
                                                        <Label>Main Title</Label>
                                                        <Input
                                                            value={formData.categoryPages[selectedCategoryTab].hero?.title || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.hero.title`, e.target.value)}
                                                            className="mt-1"
                                                            placeholder="e.g. Oncology Medications"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label>Subtitle</Label>
                                                        <Input
                                                            value={formData.categoryPages[selectedCategoryTab].hero?.subtitle || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.hero.subtitle`, e.target.value)}
                                                            className="mt-1"
                                                            placeholder="e.g. Affordable Treatments"
                                                        />
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <Label>Description</Label>
                                                        <Textarea
                                                            rows={3}
                                                            value={formData.categoryPages[selectedCategoryTab].hero?.description || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.hero.description`, e.target.value)}
                                                            className="mt-1 bg-gray-50/50"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Products Array */}
                                            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-4 mb-6">
                                                    <div>
                                                        <h4 className="text-lg font-bold text-gray-900 flex items-center">
                                                            <ListOrdered className="w-5 h-5 mr-2 text-red-600" /> Products List
                                                        </h4>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            <p className="text-sm text-gray-500">Total {formData.categoryPages[selectedCategoryTab].products?.length || 0} products in this category.</p>
                                                            <p className="text-[10px] text-gray-400 bg-gray-100 px-1 rounded border">Ctrl: {siteData.categoryPages?.[selectedCategoryTab]?.products?.length || 0}</p>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        onClick={() => {
                                                            const newProducts = [...(formData.categoryPages[selectedCategoryTab].products || [])];
                                                            newProducts.unshift({
                                                                id: Date.now().toString(),
                                                                name: 'New Product',
                                                                genericName: '',
                                                                indication: '',
                                                                price: '$0.00',
                                                                packaging: '',
                                                                description: ''
                                                            });
                                                            updateField(`categoryPages.${selectedCategoryTab}.products`, newProducts);
                                                        }}
                                                        className="bg-red-600 hover:bg-red-700 text-white mt-4 sm:mt-0 flex items-center shadow-md"
                                                    >
                                                        <Plus className="w-4 h-4 mr-2" /> Add New Product
                                                    </Button>
                                                </div>

                                                <div className="space-y-6 max-h-[800px] overflow-y-auto pr-2 rounded-lg hide-scrollbar">
                                                    {(formData.categoryPages[selectedCategoryTab].products || []).map((product, pIdx) => (
                                                        <div key={pIdx} className="bg-gray-50 p-5 border border-gray-200 rounded-xl relative group hover:border-red-200 transition-colors shadow-sm">
                                                            <p className="absolute -top-3 left-4 bg-gray-900 text-white text-[10px] uppercase font-bold px-2 py-0.5 rounded-full shadow-sm">
                                                                Product {pIdx + 1}
                                                            </p>
                                                            <button
                                                                onClick={() => {
                                                                    if (window.confirm(`Delete product "${product.name}"?`)) {
                                                                        const newProducts = formData.categoryPages[selectedCategoryTab].products.filter((_, i) => i !== pIdx);
                                                                        updateField(`categoryPages.${selectedCategoryTab}.products`, newProducts);
                                                                    }
                                                                }}
                                                                className="absolute top-4 right-4 text-gray-400 hover:text-red-600 p-1.5 bg-white shadow-sm border border-gray-200 rounded-md transition-all hover:bg-red-50 z-10"
                                                                title="Delete Product"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>

                                                            <div className="grid sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 pt-2 mb-2 pr-10">
                                                                <div>
                                                                    <Label className="text-xs text-gray-600 font-semibold mb-1 block">Brand Name</Label>
                                                                    <Input value={product.name || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.products`, pIdx, 'name', e.target.value)} className="h-9 text-sm bg-white" />
                                                                </div>
                                                                <div>
                                                                    <Label className="text-xs text-gray-600 font-semibold mb-1 block">Generic Name</Label>
                                                                    <Input value={product.genericName || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.products`, pIdx, 'genericName', e.target.value)} className="h-9 text-sm bg-white" />
                                                                </div>
                                                                <div>
                                                                    <Label className="text-xs text-gray-600 font-semibold mb-1 block">Indication (For)</Label>
                                                                    <Input value={product.indication || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.products`, pIdx, 'indication', e.target.value)} className="h-9 text-sm bg-white" placeholder="e.g. Prostate Cancer" />
                                                                </div>
                                                                <div>
                                                                    <Label className="text-xs text-gray-600 font-semibold mb-1 block">Category/Class</Label>
                                                                    <Input value={product.category || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.products`, pIdx, 'category', e.target.value)} className="h-9 text-sm bg-white" placeholder="e.g. Targeted Therapy" />
                                                                </div>
                                                                <div>
                                                                    <Label className="text-xs text-green-700 font-bold mb-1 block">Price</Label>
                                                                    <Input value={product.price || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.products`, pIdx, 'price', e.target.value)} className="h-9 text-sm text-green-700 font-bold border-green-200 bg-green-50/30" />
                                                                </div>
                                                                <div>
                                                                    <Label className="text-xs text-gray-500 font-semibold mb-1 block">Original Price (Strike)</Label>
                                                                    <Input value={product.originalPrice || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.products`, pIdx, 'originalPrice', e.target.value)} className="h-9 text-sm text-gray-500 bg-white" placeholder="e.g. $800" />
                                                                </div>
                                                                <div>
                                                                    <Label className="text-xs text-gray-600 font-semibold mb-1 block">Packaging</Label>
                                                                    <Input value={product.packaging || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.products`, pIdx, 'packaging', e.target.value)} className="h-9 text-sm bg-white" placeholder="e.g. 30 Tablets" />
                                                                </div>
                                                                <div>
                                                                    <Label className="text-xs text-gray-600 font-semibold mb-1 block">Manufacturer</Label>
                                                                    <Input value={product.manufacturer || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.products`, pIdx, 'manufacturer', e.target.value)} className="h-9 text-sm bg-white" />
                                                                </div>
                                                                <div>
                                                                    <Label className="text-xs text-red-600 font-semibold mb-1 block">Savings (e.g. 93%)</Label>
                                                                    <Input value={product.savings || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.products`, pIdx, 'savings', e.target.value)} className="h-9 text-sm text-red-600 border-red-100 bg-red-50/30" placeholder="e.g. 93%" />
                                                                </div>
                                                                <div className="sm:col-span-2 md:col-span-3 xl:col-span-4">
                                                                    <Label className="text-xs text-gray-600 font-semibold mb-1 block">Short Description</Label>
                                                                    <Textarea rows={2} value={product.description || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.products`, pIdx, 'description', e.target.value)} className="text-sm bg-white resize-none" />
                                                                </div>
                                                                <div className="sm:col-span-2">
                                                                    <Label className="text-xs text-gray-600 font-semibold mb-1 block">Dosage Information</Label>
                                                                    <Textarea rows={2} value={product.dosage || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.products`, pIdx, 'dosage', e.target.value)} className="text-sm bg-white resize-none" placeholder="e.g. As prescribed by physician..." />
                                                                </div>
                                                                <div className="sm:col-span-2">
                                                                    <Label className="text-xs text-gray-600 font-semibold mb-1 block">Storage Instructions</Label>
                                                                    <Textarea rows={2} value={product.storage || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.products`, pIdx, 'storage', e.target.value)} className="text-sm bg-white resize-none" placeholder="e.g. Store below 25°C..." />
                                                                </div>
                                                                <div className="sm:col-span-2 md:col-span-3 xl:col-span-4">
                                                                    <Label className="text-xs text-gray-600 font-semibold mb-1 block">Side Effects</Label>
                                                                    <Textarea rows={2} value={product.sideEffects || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.products`, pIdx, 'sideEffects', e.target.value)} className="text-sm bg-white resize-none" placeholder="e.g. Nausea, headache..." />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {(!formData.categoryPages[selectedCategoryTab].products || formData.categoryPages[selectedCategoryTab].products.length === 0) && (
                                                        <div className="text-center py-12 bg-gray-50 text-gray-500 border-2 border-dashed border-gray-200 rounded-xl">
                                                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm border border-gray-100">
                                                                <ListOrdered className="w-6 h-6 text-gray-400" />
                                                            </div>
                                                            <p className="font-medium text-gray-900">No products added yet</p>
                                                            <p className="text-sm mt-1">Click "Add New Product" to start building this category layout.</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* MID-PAGE CTA SECTION */}
                                            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 mt-8">
                                                <div className="flex items-center justify-between mb-8 border-b border-gray-50 pb-6">
                                                    <div>
                                                        <h3 className="text-xl font-bold text-gray-900 flex items-center">
                                                            <Layers className="w-5 h-5 mr-3 text-red-600" />
                                                            Mid-Page CTA Banner
                                                        </h3>
                                                        <p className="text-sm text-gray-500 mt-1">Edit the "Can't Find Your Medication?" section content.</p>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div className="space-y-4">
                                                        <Label className="text-sm font-semibold">Banner Title</Label>
                                                        <Input
                                                            value={formData.categoryPages[selectedCategoryTab].sectionContent?.midPageCTA?.title || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.sectionContent.midPageCTA.title`, e.target.value)}
                                                            className="bg-gray-50 border-gray-100 h-11"
                                                        />
                                                    </div>
                                                    <div className="space-y-4">
                                                        <Label className="text-sm font-semibold">WhatsApp Button Text</Label>
                                                        <Input
                                                            value={formData.categoryPages[selectedCategoryTab].sectionContent?.midPageCTA?.whatsappText || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.sectionContent.midPageCTA.whatsappText`, e.target.value)}
                                                            className="bg-gray-50 border-gray-100 h-11"
                                                        />
                                                    </div>
                                                    <div className="space-y-4 md:col-span-2">
                                                        <Label className="text-sm font-semibold">Banner Description</Label>
                                                        <Textarea
                                                            rows={3}
                                                            value={formData.categoryPages[selectedCategoryTab].sectionContent?.midPageCTA?.description || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.sectionContent.midPageCTA.description`, e.target.value)}
                                                            className="bg-gray-50 border-gray-100"
                                                        />
                                                    </div>
                                                    <div className="space-y-4">
                                                        <Label className="text-sm font-semibold">Request Link Text</Label>
                                                        <Input
                                                            value={formData.categoryPages[selectedCategoryTab].sectionContent?.midPageCTA?.requestText || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.sectionContent.midPageCTA.requestText`, e.target.value)}
                                                            className="bg-gray-50 border-gray-100 h-11"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* TESTIMONIALS & FAQ HEADERS */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                                                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                                                    <h3 className="text-lg font-bold text-gray-900 flex items-center mb-6">
                                                        <Users className="w-5 h-5 mr-3 text-red-600" />
                                                        Testimonials Heading
                                                    </h3>
                                                    <div className="space-y-6">
                                                        <div className="space-y-3">
                                                            <Label className="text-sm font-semibold">Section Title</Label>
                                                            <Input
                                                                value={formData.categoryPages[selectedCategoryTab].sectionContent?.testimonials?.title || ''}
                                                                onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.sectionContent.testimonials.title`, e.target.value)}
                                                                className="bg-gray-50 border-gray-100"
                                                            />
                                                        </div>
                                                        <div className="space-y-3">
                                                            <Label className="text-sm font-semibold">Section Description</Label>
                                                            <Textarea
                                                                rows={2}
                                                                value={formData.categoryPages[selectedCategoryTab].sectionContent?.testimonials?.description || ''}
                                                                onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.sectionContent.testimonials.description`, e.target.value)}
                                                                className="bg-gray-50 border-gray-100"
                                                            />
                                                        </div>

                                                        {/* Testimonials List Editor */}
                                                        <div className="pt-4 border-t border-gray-50 mt-4">
                                                            <div className="flex justify-between items-center mb-4">
                                                                <Label className="font-bold text-gray-700">Testimonial Cards</Label>
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        const newList = [...(formData.categoryPages[selectedCategoryTab].testimonials || [])];
                                                                        newList.unshift({
                                                                            author: "Patient Name",
                                                                            condition: "Condition Title",
                                                                            quote: "Patient experience...",
                                                                            statistic: "Savings info",
                                                                            image: "https://i.pravatar.cc/150?img=1"
                                                                        });
                                                                        updateField(`categoryPages.${selectedCategoryTab}.testimonials`, newList);
                                                                    }}
                                                                    className="h-7 text-[10px] bg-red-600 hover:bg-red-700"
                                                                >
                                                                    <Plus className="w-3 h-3 mr-1" /> Add Card
                                                                </Button>
                                                            </div>
                                                            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 hide-scrollbar">
                                                                {(formData.categoryPages[selectedCategoryTab].testimonials || []).map((t, tIdx) => (
                                                                    <div key={tIdx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 relative group">
                                                                        <button
                                                                            onClick={() => {
                                                                                const newList = formData.categoryPages[selectedCategoryTab].testimonials.filter((_, i) => i !== tIdx);
                                                                                updateField(`categoryPages.${selectedCategoryTab}.testimonials`, newList);
                                                                            }}
                                                                            className="absolute top-2 right-2 text-gray-400 hover:text-red-600"
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </button>
                                                                        <div className="grid grid-cols-2 gap-3">
                                                                            <div>
                                                                                <Label className="text-[10px]">Name</Label>
                                                                                <Input value={t.author || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.testimonials`, tIdx, 'author', e.target.value)} className="h-8 text-xs" />
                                                                            </div>
                                                                            <div>
                                                                                <Label className="text-[10px]">Condition</Label>
                                                                                <Input value={t.condition || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.testimonials`, tIdx, 'condition', e.target.value)} className="h-8 text-xs" />
                                                                            </div>
                                                                            <div>
                                                                                <Label className="text-[10px]">Savings Stat</Label>
                                                                                <Input value={t.statistic || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.testimonials`, tIdx, 'statistic', e.target.value)} className="h-8 text-xs" />
                                                                            </div>
                                                                            <div>
                                                                                <Label className="text-[10px]">Image URL</Label>
                                                                                <Input value={t.image || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.testimonials`, tIdx, 'image', e.target.value)} className="h-8 text-xs" />
                                                                            </div>
                                                                            <div className="col-span-2">
                                                                                <Label className="text-[10px]">Quote</Label>
                                                                                <Textarea value={t.quote || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.testimonials`, tIdx, 'quote', e.target.value)} className="text-xs" rows={2} />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                                                    <h3 className="text-lg font-bold text-gray-900 flex items-center mb-6">
                                                        <HelpCircle className="w-5 h-5 mr-3 text-red-600" />
                                                        FAQ Heading
                                                    </h3>
                                                    <div className="space-y-6">
                                                        <div className="space-y-3">
                                                            <Label className="text-sm font-semibold">Section Title</Label>
                                                            <Input
                                                                value={formData.categoryPages[selectedCategoryTab].sectionContent?.faqs?.title || ''}
                                                                onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.sectionContent.faqs.title`, e.target.value)}
                                                                className="bg-gray-50 border-gray-100"
                                                            />
                                                        </div>
                                                        <div className="space-y-3">
                                                            <Label className="text-sm font-semibold">Section Description</Label>
                                                            <Textarea
                                                                rows={2}
                                                                value={formData.categoryPages[selectedCategoryTab].sectionContent?.faqs?.description || ''}
                                                                onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.sectionContent.faqs.description`, e.target.value)}
                                                                className="bg-gray-50 border-gray-100"
                                                            />
                                                        </div>

                                                        {/* FAQ List Editor */}
                                                        <div className="pt-4 border-t border-gray-50 mt-4">
                                                            <div className="flex justify-between items-center mb-4">
                                                                <Label className="font-bold text-gray-700">Individual FAQs</Label>
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        const newList = [...(formData.categoryPages[selectedCategoryTab].faqs || [])];
                                                                        newList.unshift({
                                                                            question: "New Question",
                                                                            answer: "Provide the answer here..."
                                                                        });
                                                                        updateField(`categoryPages.${selectedCategoryTab}.faqs`, newList);
                                                                    }}
                                                                    className="h-7 text-[10px] bg-red-600 hover:bg-red-700"
                                                                >
                                                                    <Plus className="w-3 h-3 mr-1" /> Add FAQ
                                                                </Button>
                                                            </div>
                                                            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 hide-scrollbar">
                                                                {(formData.categoryPages[selectedCategoryTab].faqs || []).map((faq, fIdx) => (
                                                                    <div key={fIdx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 relative group">
                                                                        <button
                                                                            onClick={() => {
                                                                                const newList = formData.categoryPages[selectedCategoryTab].faqs.filter((_, i) => i !== fIdx);
                                                                                updateField(`categoryPages.${selectedCategoryTab}.faqs`, newList);
                                                                            }}
                                                                            className="absolute top-2 right-2 text-gray-400 hover:text-red-600"
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </button>
                                                                        <div className="space-y-2 pr-4">
                                                                            <div>
                                                                                <Label className="text-[10px]">Question</Label>
                                                                                <Input value={faq.question || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.faqs`, fIdx, 'question', e.target.value)} className="h-8 text-xs" />
                                                                            </div>
                                                                            <div>
                                                                                <Label className="text-[10px]">Answer</Label>
                                                                                <Textarea value={faq.answer || ''} onChange={(e) => updateArrayItem(`categoryPages.${selectedCategoryTab}.faqs`, fIdx, 'answer', e.target.value)} className="text-xs" rows={2} />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* FINAL CTA SECTION */}
                                            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 mt-8 mb-8">
                                                <div className="flex items-center justify-between mb-8 border-b border-gray-50 pb-6">
                                                    <div>
                                                        <h3 className="text-xl font-bold text-gray-900 flex items-center">
                                                            <PhoneCall className="w-5 h-5 mr-3 text-red-600" />
                                                            Final Bottom CTA Banner
                                                        </h3>
                                                        <p className="text-sm text-gray-500 mt-1">Edit the "Ready to Save?" section at the bottom of the page.</p>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                    <div className="space-y-4">
                                                        <Label className="text-sm font-semibold">Banner Title</Label>
                                                        <Input
                                                            value={formData.categoryPages[selectedCategoryTab].sectionContent?.finalCTA?.title || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.sectionContent.finalCTA.title`, e.target.value)}
                                                            className="bg-gray-50 border-gray-100 h-11"
                                                        />
                                                    </div>
                                                    <div className="space-y-4">
                                                        <Label className="text-sm font-semibold">WhatsApp Button Text</Label>
                                                        <Input
                                                            value={formData.categoryPages[selectedCategoryTab].sectionContent?.finalCTA?.whatsappText || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.sectionContent.finalCTA.whatsappText`, e.target.value)}
                                                            className="bg-gray-50 border-gray-100 h-11"
                                                        />
                                                    </div>
                                                    <div className="space-y-4 md:col-span-2">
                                                        <Label className="text-sm font-semibold">Banner Description</Label>
                                                        <Textarea
                                                            rows={3}
                                                            value={formData.categoryPages[selectedCategoryTab].sectionContent?.finalCTA?.description || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.sectionContent.finalCTA.description`, e.target.value)}
                                                            className="bg-gray-50 border-gray-100"
                                                        />
                                                    </div>
                                                    <div className="space-y-4">
                                                        <Label className="text-sm font-semibold">Consultation Link Text</Label>
                                                        <Input
                                                            value={formData.categoryPages[selectedCategoryTab].sectionContent?.finalCTA?.requestText || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.sectionContent.finalCTA.requestText`, e.target.value)}
                                                            className="bg-gray-50 border-gray-100 h-11"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* FOOTER SECTION */}
                                            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                                                <div className="flex items-center justify-between mb-8 border-b border-gray-50 pb-6">
                                                    <div>
                                                        <h3 className="text-xl font-bold text-gray-900 flex items-center">
                                                            <Layout className="w-5 h-5 mr-3 text-red-600" />
                                                            Category Footer Layout (Minimalist)
                                                        </h3>
                                                        <p className="text-sm text-gray-500 mt-1">Manage the centered brand logo and copyright info for this category.</p>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                    <div className="space-y-4">
                                                        <Label className="text-sm font-semibold">Brand Name</Label>
                                                        <Input
                                                            value={formData.categoryPages[selectedCategoryTab].footerMinimal?.brandName || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.footerMinimal.brandName`, e.target.value)}
                                                            className="bg-gray-50 border-gray-100 h-11"
                                                        />
                                                    </div>
                                                    <div className="space-y-4">
                                                        <Label className="text-sm font-semibold">Copyright Year</Label>
                                                        <Input
                                                            value={formData.categoryPages[selectedCategoryTab].footerMinimal?.copyrightYear || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.footerMinimal.copyrightYear`, e.target.value)}
                                                            className="bg-gray-50 border-gray-100 h-11"
                                                        />
                                                    </div>
                                                    <div className="md:col-span-2 lg:col-span-3 space-y-4">
                                                        <Label className="text-sm font-semibold">License / rights Info</Label>
                                                        <Input
                                                            value={formData.categoryPages[selectedCategoryTab].footerMinimal?.licenseInfo || ''}
                                                            onChange={(e) => updateField(`categoryPages.${selectedCategoryTab}.footerMinimal.licenseInfo`, e.target.value)}
                                                            className="bg-gray-50 border-gray-100 h-11"
                                                            placeholder="e.g. All rights reserved. | Licensed International Pharmacy"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    )}

                                </div>
                            </TabsContent>

                            {/* SECURITY TAB */}
                            <TabsContent value="security">
                                <div className="space-y-6 max-w-xl">
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
                                            <Lock className="mr-2 w-6 h-6" /> Admin Security Settings
                                        </h3>
                                        <p className="text-sm text-gray-500 border-b border-gray-100 pb-6">Update your admin login password. Changes will take effect after saving.</p>
                                    </div>

                                    <form onSubmit={handlePasswordChange} className="space-y-4 pt-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="currentPass">Current Admin Password</Label>
                                            <Input
                                                id="currentPass"
                                                type="password"
                                                required
                                                value={securityForm.currentPassword}
                                                onChange={(e) => setSecurityForm({ ...securityForm, currentPassword: e.target.value })}
                                            />
                                        </div>

                                        <div className="space-y-2 pt-2">
                                            <Label htmlFor="newPass">New Password</Label>
                                            <Input
                                                id="newPass"
                                                type="password"
                                                required
                                                value={securityForm.newPassword}
                                                onChange={(e) => setSecurityForm({ ...securityForm, newPassword: e.target.value })}
                                                placeholder="At least 6 characters"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="confirmPass">Confirm New Password</Label>
                                            <Input
                                                id="confirmPass"
                                                type="password"
                                                required
                                                value={securityForm.confirmPassword}
                                                onChange={(e) => setSecurityForm({ ...securityForm, confirmPassword: e.target.value })}
                                            />
                                        </div>

                                        <div className="space-y-2 pt-4">
                                            <Label htmlFor="recoveryEmail">Recovery Email (For OTP verification)</Label>
                                            <Input
                                                id="recoveryEmail"
                                                type="email"
                                                value={formData.general?.recoveryEmail || ''}
                                                onChange={(e) => updateField('general.recoveryEmail', e.target.value)}
                                                placeholder="admin@example.com"
                                            />
                                            <p className="text-xs text-gray-500">OTP will be sent to this email during password recovery.</p>
                                        </div>

                                        <div className="pt-4">
                                            <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto">
                                                Update Password
                                            </Button>
                                        </div>
                                    </form>

                                    <div className="mt-8 bg-amber-50 border border-amber-200 p-4 rounded-lg">
                                        <p className="text-sm text-amber-800 flex items-start">
                                            <AlertTriangle className="w-5 h-5 mr-3 mt-0.5 shrink-0" />
                                            <span>
                                                <strong>Note:</strong> Password change requires you to click <strong>"Save All Changes"</strong> at the top to permanently apply the new password. If you forget your password, you will need to manually clear your browser's local storage or use the "Reset to Defaults" option (if accessible).
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </TabsContent>

                            {/* DANGER/ADVANCED TAB */}
                            <TabsContent value="danger">
                                <div className="space-y-6 max-w-4xl">
                                    <div>
                                        <h3 className="text-2xl font-bold text-red-600 mb-2 flex items-center">
                                            <AlertTriangle className="mr-2 w-6 h-6" /> Danger Zone
                                        </h3>
                                        <p className="text-sm text-red-500 border-b border-red-100 pb-6">These actions are destructive and cannot be undone.</p>
                                    </div>

                                    <div className="pt-2">
                                        <div className="bg-red-50 p-6 rounded-xl border border-red-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                            <div>
                                                <h4 className="font-bold text-lg text-red-900 mb-1">Factory Reset Default Data</h4>
                                                <p className="text-sm text-red-700">This will wipe all modifications made in this Admin Panel and restore the site to the original hardcoded configuration.</p>
                                            </div>
                                            <Button onClick={handleReset} variant="outline" className="border-red-500 bg-white text-red-600 hover:bg-red-600 hover:text-white shrink-0 shadow-sm">
                                                Reset to Defaults
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </TabsContent>

                        </Tabs>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminPanel;
